import { useSearchParams } from 'react-router';

import { ClientEnvironment, normaliseSlot, type ClientTarget } from '../schemas/target';

/**
 * The environment scope, held in the URL.
 *
 * ## Why the URL and not a context
 *
 * Which backend you are looking at is the single most important fact on the
 * screen, and it survives a reload, a bookmark and a link pasted into Slack
 * only if it is addressable. A provider would lose it on every refresh and
 * quietly reset to the default — which, for a screen that also *writes*, means
 * an operator who reloaded on production could create a client in dev without
 * noticing anything changed.
 *
 * ## Why it is NOT in the app-wide topbar
 *
 * It was, in the prototype, where clients were the only screens. In this app
 * the topbar is shared with Feature Flags, which has an environment concept of
 * its own and a completely different shape — one row in one table, four values
 * compared side by side. A global switcher would read as scoping that too. So
 * this belongs to the clients screens and travels in their URL.
 */

const DEFAULT_ENVIRONMENT = 'dev';

export interface ClientTargetState {
    readonly target: ClientTarget;
    /** The slot as typed — see {@link isSlotSettled}. */
    readonly slotDraft: string;
    readonly setEnvironment: (env: ClientTarget['env']) => void;
    readonly setSlotDraft: (slot: string) => void;
    /** Commits the typed slot, which is what actually changes the target. */
    readonly applySlot: () => void;
    readonly discardSlotDraft: () => void;
    /** `eph` with no slot committed: there is nothing to address yet. */
    readonly isSlotMissing: boolean;
    /** Something typed that has not been applied — the screen still shows the old target. */
    readonly isSlotDirty: boolean;
}

/**
 * `dev` rather than `prd`. A default that lands on production is a default that
 * eventually creates a client there by accident; the safe end of the ladder is
 * the one to guess.
 */
function readTarget(params: URLSearchParams): ClientTarget {
    const parsed = ClientEnvironment.safeParse(params.get('env'));
    const env = parsed.success ? parsed.data : DEFAULT_ENVIRONMENT;
    const slot = normaliseSlot(params.get('slot') ?? '');

    return env === 'eph' && slot.length > 0 ? { env, slot } : { env };
}

export function useClientTarget(): ClientTargetState {
    const [params, setParams] = useSearchParams();
    const target = readTarget(params);

    // Draft and committed value are two things on purpose. Wired to the same
    // one, typing `dev1` would query `d`, `de`, `dev` and `dev1` — three
    // targets that do not exist — and there would be no moment at which the
    // slot was chosen. The target changes on Enter or blur; until then the
    // screen keeps showing the old one, and says so.
    const slotDraft = params.get('slotDraft') ?? target.slot ?? '';

    const write = (next: { env?: ClientTarget['env']; slot?: string; draft?: string }) => {
        setParams(
            (current) => {
                const updated = new URLSearchParams(current);
                if (next.env !== undefined) updated.set('env', next.env);
                if (next.slot !== undefined) {
                    if (next.slot) updated.set('slot', next.slot);
                    else updated.delete('slot');
                }
                if (next.draft !== undefined) {
                    if (next.draft) updated.set('slotDraft', next.draft);
                    else updated.delete('slotDraft');
                }
                return updated;
            },
            { replace: true },
        );
    };

    const committed = target.slot ?? '';
    const draft = normaliseSlot(slotDraft);

    return {
        target,
        slotDraft: draft,
        isSlotMissing: target.env === 'eph' && committed.length === 0,
        isSlotDirty: target.env === 'eph' && draft !== committed,

        setEnvironment: (env) => {
            // Leaving `eph` and coming back must not inherit a half-typed slot.
            write({ env, ...(env === 'eph' ? {} : { slot: '', draft: '' }) });
        },
        setSlotDraft: (slot) => {
            write({ draft: normaliseSlot(slot) });
        },
        applySlot: () => {
            write({ slot: draft, draft: '' });
        },
        discardSlotDraft: () => {
            write({ draft: '' });
        },
    };
}
